import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/Card';
import { ActivityItem, ActivityType } from '@/types/dashboard.types';

interface ActivityFeedProps {
  activities: ActivityItem[];
  isLoading?: boolean;
  limit?: number;
}

// Activity type configuration
const activityConfig: Record<
  ActivityType,
  { icon: React.ReactNode; bgColor: string; iconColor: string }
> = {
  lead_created: {
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
      </svg>
    ),
    bgColor: 'bg-blue-100',
    iconColor: 'text-blue-600',
  },
  lead_status_changed: {
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
      </svg>
    ),
    bgColor: 'bg-purple-100',
    iconColor: 'text-purple-600',
  },
  visit_scheduled: {
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
    ),
    bgColor: 'bg-indigo-100',
    iconColor: 'text-indigo-600',
  },
  visit_completed: {
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    bgColor: 'bg-green-100',
    iconColor: 'text-green-600',
  },
  referral_received: {
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16l-4-4m0 0l4-4m-4 4h18" />
      </svg>
    ),
    bgColor: 'bg-cyan-100',
    iconColor: 'text-cyan-600',
  },
  referral_sent: {
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
      </svg>
    ),
    bgColor: 'bg-teal-100',
    iconColor: 'text-teal-600',
  },
  referral_accepted: {
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
      </svg>
    ),
    bgColor: 'bg-emerald-100',
    iconColor: 'text-emerald-600',
  },
  topup_approved: {
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    bgColor: 'bg-amber-100',
    iconColor: 'text-amber-600',
  },
  commission_earned: {
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    bgColor: 'bg-green-100',
    iconColor: 'text-green-600',
  },
};

const formatTimeAgo = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (diffInSeconds < 60) {
    return 'Just now';
  }
  if (diffInSeconds < 3600) {
    const mins = Math.floor(diffInSeconds / 60);
    return `${mins} min${mins > 1 ? 's' : ''} ago`;
  }
  if (diffInSeconds < 86400) {
    const hours = Math.floor(diffInSeconds / 3600);
    return `${hours} hour${hours > 1 ? 's' : ''} ago`;
  }
  if (diffInSeconds < 604800) {
    const days = Math.floor(diffInSeconds / 86400);
    return `${days} day${days > 1 ? 's' : ''} ago`;
  }

  return date.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
  });
};

const LoadingSkeleton: React.FC = () => (
  <div className="space-y-4">
    {[1, 2, 3, 4, 5].map((i) => (
      <div key={i} className="flex items-start gap-3 animate-pulse">
        <div className="w-8 h-8 rounded-full bg-secondary-200"></div>
        <div className="flex-1">
          <div className="h-4 bg-secondary-200 rounded w-3/4 mb-2"></div>
          <div className="h-3 bg-secondary-100 rounded w-1/2"></div>
        </div>
        <div className="h-3 bg-secondary-100 rounded w-16"></div>
      </div>
    ))}
  </div>
);

const ActivityItemRow: React.FC<{ activity: ActivityItem }> = ({ activity }) => {
  const navigate = useNavigate();
  const config = activityConfig[activity.type] || activityConfig.lead_created;

  const handleClick = () => {
    // Navigate based on activity type
    if (activity.metadata?.lead_id) {
      navigate(`/leads/${activity.metadata.lead_id}`);
    } else if (activity.metadata?.visit_id) {
      navigate(`/visits/${activity.metadata.visit_id}`);
    } else if (activity.metadata?.referral_id) {
      navigate(`/referrals/${activity.metadata.referral_id}`);
    }
  };

  const isClickable = activity.metadata?.lead_id || activity.metadata?.visit_id || activity.metadata?.referral_id;

  return (
    <div
      onClick={isClickable ? handleClick : undefined}
      className={`flex items-start gap-3 py-3 border-b border-secondary-100 last:border-b-0 ${
        isClickable ? 'cursor-pointer hover:bg-secondary-50 -mx-2 px-2 rounded-lg' : ''
      } transition-colors`}
    >
      {/* Icon */}
      <div className={`p-2 rounded-full ${config.bgColor} ${config.iconColor} flex-shrink-0`}>
        {config.icon}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-secondary-900">{activity.title}</p>
        <p className="text-sm text-secondary-500 truncate">{activity.description}</p>
      </div>

      {/* Time */}
      <span className="text-xs text-secondary-400 whitespace-nowrap flex-shrink-0">
        {formatTimeAgo(activity.created_at)}
      </span>
    </div>
  );
};

export const ActivityFeed: React.FC<ActivityFeedProps> = ({
  activities,
  isLoading = false,
  limit = 10,
}) => {
  const navigate = useNavigate();
  const displayActivities = activities.slice(0, limit);

  return (
    <Card className="h-full" padding="none">
      {/* Header */}
      <div className="px-6 py-4 border-b border-secondary-200">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-secondary-900">Recent Activity</h3>
          {activities.length > 0 && (
            <span className="text-xs text-secondary-400 bg-secondary-100 px-2 py-1 rounded-full">
              {activities.length} new
            </span>
          )}
        </div>
      </div>

      {/* Activity List */}
      <div className="px-6 py-2">
        {isLoading ? (
          <LoadingSkeleton />
        ) : displayActivities.length === 0 ? (
          <div className="text-center py-8">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-secondary-100 mb-3">
              <svg
                className="w-6 h-6 text-secondary-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <p className="text-sm text-secondary-500">No recent activity</p>
          </div>
        ) : (
          <div>
            {displayActivities.map((activity) => (
              <ActivityItemRow key={activity.id} activity={activity} />
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      {!isLoading && activities.length > 0 && (
        <div className="px-6 py-3 border-t border-secondary-200">
          <button
            onClick={() => navigate('/activity')}
            className="text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors"
          >
            View all activity
          </button>
        </div>
      )}
    </Card>
  );
};

export default ActivityFeed;
